import { useRef, useMemo } from 'react';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Modal from '@sdk/components/ui/modal/Modal';
import ModalFooterActionButtons from '@sdk/components/ui/modal/ModalFooterActionButtons';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import type { MissedBreaks } from '@sdk/automatedBreaks/types/MissedBreaks';
import type { AttestationForRequest } from '@sdk/automatedBreaks/types/Attestation';
import { AttestationOption } from '@sdk/automatedBreaks/types/Attestation';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import { getShiftActualCurrentDuration } from '@sdk/automatedBreaks/utils';
import type { Shift } from '@sdk/automatedBreaks/types/Shift';
import getAllBreaksLists from 'sling-sdk/src/automatedBreaks/helpers/getAllBreaksLists';

import { ScrollableArea } from '@web/automatedBreaks/breakAttestations/BreakAttestationsModal/breakAttestationsModal.styles';
import { useBreakAttestationsModal } from '@web/automatedBreaks/breakAttestations/BreakAttestationsModal/breakAttestationsModal.hooks';
import AttestationItem from '@web/automatedBreaks/breakAttestations/AttestationItem/AttestationItem';
import TimeBlueSvg from '@web/assets/svg/Icons/32/time-blue.svg';

import { shouldSkipOrdinals } from './breakAttestationModal.utils';

type Props = {
  isVisible: boolean;
  accept: (attestations: AttestationForRequest[]) => void;
  decline: () => void;
  missedBreaks: MissedBreaks | null;
  orgSettings: OrgSettings;
  shift: Shift;
};

const BreakAttestationsModal = ({
  isVisible,
  accept,
  decline,
  missedBreaks,
  orgSettings,
  shift,
}: Props) => {
  const config = useRef({ noDismiss: true }).current;

  const {
    attestationOptions,
    setAttestationOptions,
    attestationTexts,
    setAttestationTexts,
    missedBreaksArray,
    attestations,
    isValid,
    onIsDropdownOpenChange,
    isScrollLocked,
  } = useBreakAttestationsModal(missedBreaks);

  const groupedBreaksListFromLastProjection = useMemo(() => {
    const allBreaksLists = getAllBreaksLists(shift.timesheetEntries, shift.timesheetProjections);
    return allBreaksLists[allBreaksLists.length - 1];
  }, [shift.timesheetEntries, shift.timesheetProjections]);

  const shiftActualDurationInMinutes = getShiftActualCurrentDuration(shift, orgSettings);

  return (
    <>
      <Modal fixed config={config} isVisible={isVisible}>
        <ModalBodyWithHeader
          width={620}
          titleThemeColor={ThemeColorType.primary}
          titleText={gettext('Please Confirm')}
          titleIcon={<TimeBlueSvg />}
          close={decline}>
          <ScrollableArea scrollLocked={isScrollLocked}>
            {missedBreaksArray.map((missedBreak, index) => (
              <AttestationItem
                key={missedBreak.key}
                shiftActualDurationInMinutes={shiftActualDurationInMinutes}
                missedBreak={missedBreak}
                orgSettings={orgSettings}
                option={attestationOptions[missedBreak.key]}
                setOption={(option: AttestationOption) => {
                  setAttestationOptions({ ...attestationOptions, [missedBreak.key]: option });
                }}
                text={attestationTexts[missedBreak.key]}
                setText={(text: string) => {
                  setAttestationTexts({ ...attestationTexts, [missedBreak.key]: text });
                }}
                withSeparator={index < missedBreaksArray.length - 1}
                skipOrdinals={shouldSkipOrdinals(missedBreak, groupedBreaksListFromLastProjection)}
                onIsOpenChange={onIsDropdownOpenChange}
              />
            ))}
          </ScrollableArea>
          <ModalFooterActionButtons
            mTop={20}
            cancel={decline}
            submit={() => accept(attestations)}
            isSubmitAsync={false}
            cancelText={gettext('Back')}
            submitText={gettext('Clock out')}
            submitButtonType={ButtonType.success}
            buttonWidth={160}
            isSubmitActive={isValid}
          />
        </ModalBodyWithHeader>
      </Modal>
    </>
  );
};

export default BreakAttestationsModal;
